import React, { useState } from 'react';
import Modal from '../Modal';
import { useToast } from '../Toast';
import { useUpsertCredentialMutation } from '../../store/api/localApi';
import { CredentialType } from '../../types';
import CredentialAttachmentInput from './CredentialAttachmentInput';

interface CredentialFormProps {
  isOpen: boolean;
  onClose: () => void;
  staffUserId: string;
  /** Whether the caller is adding their own credential (self-submit). Purely
   *  informational copy — the server forces submittedBySelf/verified from the
   *  resolved caller identity regardless of what this component sends. */
  isSelf: boolean;
}

const CREDENTIAL_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: CredentialType.FIRST_AID, label: 'First Aid' },
  { value: CredentialType.CHILD_PROTECTION, label: 'Child Protection' },
  { value: CredentialType.QUALIFICATION, label: 'Qualification' },
  { value: CredentialType.FIRE_SAFETY, label: 'Fire Safety' },
  { value: CredentialType.MANUAL_HANDLING, label: 'Manual Handling' },
  { value: CredentialType.OTHER, label: 'Other' },
];

/**
 * Add a credential — usable by an admin (for anyone) or by a staff member
 * for themselves. A self-submitted credential always lands unverified; the
 * server forces `submittedBySelf`/`verified` from the resolved caller
 * identity, never from the request body, so there is nothing to send here
 * even though the copy differs by audience.
 */
const CredentialForm: React.FC<CredentialFormProps> = ({
  isOpen,
  onClose,
  staffUserId,
  isSelf,
}) => {
  const { showSuccess, showError } = useToast();
  const [upsertCredential, { isLoading }] = useUpsertCredentialMutation();

  const [credentialType, setCredentialType] = useState<string>(CredentialType.FIRST_AID);
  const [title, setTitle] = useState('');
  const [issuer, setIssuer] = useState('');
  const [awardedDate, setAwardedDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [attachmentS3Key, setAttachmentS3Key] = useState<string | undefined>(undefined);
  const [isUploading, setIsUploading] = useState(false);

  const resetForm = () => {
    setCredentialType(CredentialType.FIRST_AID);
    setTitle('');
    setIssuer('');
    setAwardedDate('');
    setExpiryDate('');
    setAttachmentS3Key(undefined);
    setIsUploading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await upsertCredential({
        staffUserId,
        credentialType,
        title: title.trim(),
        ...(issuer.trim() ? { issuer: issuer.trim() } : {}),
        ...(awardedDate ? { awardedDate } : {}),
        ...(expiryDate ? { expiryDate } : {}),
        ...(attachmentS3Key ? { attachmentS3Key } : {}),
      }).unwrap();
      showSuccess(isSelf ? 'Credential added — an admin will verify it' : 'Credential added');
      resetForm();
      onClose();
    } catch (err: any) {
      showError(err.data?.error || 'Failed to add credential');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Credential" testId="credential-form-modal">
      <form onSubmit={handleSubmit} className="space-y-4">
        {isSelf && (
          <p className="text-sm text-gray-600">
            This will be recorded as not yet verified. An admin can verify it later.
          </p>
        )}
        <div>
          <label htmlFor="credential-type" className="block text-sm font-medium text-gray-700">
            Type
          </label>
          <select
            id="credential-type"
            data-testid="credential-type-select"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={credentialType}
            onChange={(e) => setCredentialType(e.target.value)}
          >
            {CREDENTIAL_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="credential-title" className="block text-sm font-medium text-gray-700">
            Title
          </label>
          <input
            id="credential-title"
            type="text"
            required
            data-testid="credential-title-input"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="credential-issuer" className="block text-sm font-medium text-gray-700">
            Issuer
          </label>
          <input
            id="credential-issuer"
            type="text"
            data-testid="credential-issuer-input"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={issuer}
            onChange={(e) => setIssuer(e.target.value)}
          />
        </div>
        <div>
          <label
            htmlFor="credential-awarded-date"
            className="block text-sm font-medium text-gray-700"
          >
            Awarded date
          </label>
          <input
            id="credential-awarded-date"
            type="date"
            data-testid="credential-awarded-date-input"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={awardedDate}
            onChange={(e) => setAwardedDate(e.target.value)}
          />
        </div>
        <div>
          <label
            htmlFor="credential-expiry-date"
            className="block text-sm font-medium text-gray-700"
          >
            Expiry date
            <span className="ml-1 text-xs font-normal text-gray-500">
              (leave blank if it doesn't expire)
            </span>
          </label>
          <input
            id="credential-expiry-date"
            type="date"
            data-testid="credential-expiry-date-input"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
          />
        </div>
        <CredentialAttachmentInput
          staffUserId={staffUserId}
          onChange={setAttachmentS3Key}
          onUploadingChange={setIsUploading}
          inputTestId="credential-attachment-input"
        />
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || isUploading || !title.trim()}
            data-testid="credential-form-submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {isUploading ? 'Uploading...' : isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CredentialForm;
